/**
 * Dynamic favicon updates the `#dynamic-favicon` link only.
 * Static `<link rel="icon" href="...png">` in `public/index.html` stay unchanged for crawlers (e.g. Google).
 */

import { parseColor } from './colorUtils.js';

const DYNAMIC_FAVICON_ID = 'dynamic-favicon';

function getOrCreateDynamicFaviconLink(): HTMLLinkElement {
  let link = document.getElementById(DYNAMIC_FAVICON_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = DYNAMIC_FAVICON_ID;
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Generates a favicon with a colored circle background and star icon
 */
export function updateFavicon(primaryHex: string, starHex: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    ctx.fillStyle = primaryHex;
    ctx.beginPath();
    ctx.arc(16, 16, 16, 0, 2 * Math.PI);
    ctx.fill();

    const starSvg = `
      <svg width="32" height="32" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M88.9301 39.1264L92.3879 54.2759L106.388 47.5337C116.897 42.4727 122.152 39.9422 124.443 40.802C126.415 41.5422 127.822 43.3058 128.105 45.3934C128.433 47.8183 124.797 52.3781 117.524 61.4977L107.836 73.6467L121.836 80.3888C132.345 85.4498 137.6 87.9803 138.356 90.3076C139.007 92.3111 138.505 94.5103 137.049 96.033C135.358 97.8017 129.526 97.8017 117.861 97.8017H102.322L105.78 112.951C108.376 124.323 109.674 130.009 108.325 132.051C107.165 133.81 105.133 134.788 103.034 134.599C100.597 134.38 96.9609 129.82 89.6882 120.701L79.9998 108.552L70.3114 120.701C63.0387 129.82 59.4023 134.38 56.9652 134.599C54.867 134.788 52.8346 133.81 51.6741 132.051C50.326 130.009 51.6238 124.323 54.2194 112.951L57.6772 97.8017H42.1382C30.4737 97.8017 24.6414 97.8017 22.9504 96.033C21.4946 94.5103 20.9926 92.3111 21.6436 90.3076C22.3998 87.9804 27.6545 85.4498 38.1638 80.3888L52.164 73.6467L42.4755 61.4977C35.2028 52.3781 31.5665 47.8183 31.895 45.3934C32.1778 43.3058 33.5842 41.5422 35.5565 40.802C37.8475 39.9422 43.1022 42.4727 53.6115 47.5337L67.6117 54.2758L71.0694 39.1264C73.665 27.7544 74.9628 22.0684 77.0634 20.8133C78.8719 19.7328 81.1277 19.7328 82.9361 20.8133C85.0367 22.0684 86.3345 27.7544 88.9301 39.1264Z" fill="${starHex}"/>
      </svg>
    `;

    const img = new Image();
    const svgBlob = new Blob([starSvg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, 32, 32);
      const dataUrl = canvas.toDataURL('image/png');
      const faviconLink = getOrCreateDynamicFaviconLink();
      faviconLink.href = dataUrl;

      let themeColorMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement;
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.name = 'theme-color';
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.content = primaryHex;

      URL.revokeObjectURL(url);
      resolve();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG image'));
    };

    img.src = url;
  });
}

export function getHexFromCSSVar(cssVar: string): string {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);
  const colorValue = computedStyle.getPropertyValue(cssVar).trim();

  if (!colorValue) {
    return '#FF8C00';
  }

  if (colorValue.startsWith('#')) {
    return colorValue;
  }

  const parsed = parseColor(colorValue) as any;
  if (parsed && parsed.isValid && parsed.hex) {
    return parsed.hex;
  }

  return '#FF8C00';
}
